import { Link } from "react-router-dom";
import { SubPageShell, PageFaq, ProfessionalBadge } from "@/components/site/SubPage";
import { PhotoCheckForm, PhotoQuiz } from "@/components/site/PhotoCheck";
import { useSeo } from "@/lib/useSeo";
import { scrollToId, ARCHIFOTOGRAFIA_URL } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const FAQ = [
  {
    q: "Le foto restano mie?",
    a: "Sì. Le usi dove vuoi e per sempre: Booking, Airbnb, il tuo sito, i social. Sono un investimento tuo, non una licenza a tempo.",
  },
  {
    q: "Quanto dura lo shooting?",
    a: "Dipende dalla struttura: un bilocale non è una villa con dieci camere. Prima di iniziare lo definiamo insieme, con tempi e costi chiari.",
  },
  {
    q: "Devo preparare la casa da solo?",
    a: "No. Prima dello shooting ti diciamo esattamente cosa sistemare, e se serve interveniamo con un home staging leggero: piccoli spostamenti che cambiano come la casa si presenta in foto.",
  },
  {
    q: "Lavorate fuori dalla Sicilia?",
    a: "Giovanni copre tutta la Sicilia e le isole minori. Per strutture fuori regione valutiamo insieme: scrivici e vediamo se ha senso.",
  },
];

export default function FotografoPage() {
  useSeo({
    title: "Fotografo per B&B e case vacanze in Sicilia | YU YieldUp",
    description:
      "Fotografia professionale di interni per hotel, B&B e case vacanze in tutta la Sicilia: shooting, drone, video e home staging con Archifotografia, dentro la strategia di vendita del tuo annuncio.",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Fotografia professionale per strutture ricettive in Sicilia",
          provider: { "@type": "Person", name: "Giovanni Costagliola", brand: { "@type": "Brand", name: "Archifotografia" } },
          areaServed: { "@type": "AdministrativeArea", name: "Sicilia" },
          description:
            "Fotografia di interni e architettura per hotel, B&B, ville e case vacanze: shooting, riprese con drone, video e home staging.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      ],
    },
  });

  return (
    <SubPageShell callSource="fotografo">
      <section data-testid="fotografo-page" className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <p className="section-kicker">Servizi in Sicilia</p>
        <h1 className="mt-4 font-heading text-3xl font-black uppercase leading-[1.02] tracking-tighter text-[#FAF7F2] sm:text-5xl">
          Fotografo per strutture ricettive in Sicilia
        </h1>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0] sm:text-lg">
          Su Booking e Airbnb l'ospite decide in pochi secondi, guardando le foto. Non il prezzo, non la descrizione: le foto. Se le tue non vendono, stai regalando prenotazioni ai concorrenti della tua stessa zona.
        </p>
        <ProfessionalBadge testid="fotografo-badge" photo="/images/giovanni.jpg" name="Giovanni Costagliola, fotografo" caption="Archifotografia, Palermo. Quello che scatta le tue foto." />
        <PhotoCheckForm />

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Cosa facciamo</h2>
        <ul className="mt-6 space-y-4">
          {[
            "Shooting fotografico di interni ed esterni, pensato per i portali di prenotazione",
            "Riprese con drone per ville, piscine e contesti che dall'alto cambiano tutto",
            "Video della struttura per annunci e social",
            "Home staging leggero prima dello shooting: la casa presentata al meglio di quello che è",
            "Selezione e ordine delle foto per l'annuncio: la prima foto è quella che decide il click",
          ].map((item) => (
            <li key={item} className="flex gap-4 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
              <span className="shrink-0 font-heading font-black text-[#E05A2C]">—</span>
              {item}
            </li>
          ))}
        </ul>

        <div data-testid="fotografo-prezzo" className="mt-12 border border-[#383029] bg-[#16110E] p-6 sm:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Foto che vendono</p>
            <p className="font-heading text-3xl font-black tracking-tighter text-[#E05A2C]">da 400 €</p>
          </div>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Lo stesso prezzo che trovi in home. Cosa include:
          </p>
          <ul className="mt-4 space-y-3">
            {[
              "Shooting di mezza giornata nella tua struttura, interni ed esterni",
              "Fino a 30 foto selezionate e ottimizzate per Booking e Airbnb, consegnate pronte da caricare",
              "Ordine delle foto nell'annuncio: quale apre, quali chiudono, quali togliere",
              "Titolo e descrizione rifatti attorno alle nuove foto, con Luciano",
              "Drone e video: su richiesta, quotati nel preventivo prima dello shooting",
            ].map((item) => (
              <li key={item} className="flex gap-4 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
                <span className="shrink-0 font-heading font-black text-[#E05A2C]">—</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-[#686054]">
            Il prezzo finale dipende dalla struttura: lo confermiamo prima, niente sorprese.
          </p>
        </div>

        <div data-testid="fotografo-offerta" className="mt-6 border border-[#4D2C20] bg-[#2B1B15] p-6 sm:p-8">
          <p className="section-kicker">Gratis, per cominciare</p>
          <p className="mt-3 font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">
            Mandaci il link del tuo annuncio: ti diciamo quali 3 foto cambiare.
          </p>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Giovanni guarda le foto che hai online oggi e ti indica le tre che ti stanno costando più click. Senza impegno: se sono già giuste, te lo diciamo.
          </p>
          <button
            data-testid="fotografo-cta-tre-foto"
            onClick={() => scrollToId("check-foto")}
            className="btn-glow mt-6 inline-flex min-h-[44px] items-center rounded-full bg-[#E05A2C] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
          >
            Fai valutare le foto
          </button>
        </div>

        <PhotoQuiz />
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Giovanni è un fotografo di interni, hotel e architettura con base a Palermo e oltre quindici anni di esperienza: ha fotografato più di mille alloggi turistici e ha collaborato con realtà come Airbnb Plus, Tripadvisor e Plum Guide. Nel 2020 ha pubblicato il manuale internazionale <span className="italic">Picture Perfect Properties</span>. Con Archifotografia lavora in tutta la Sicilia e nelle isole minori.
        </p>
        <p className="mt-4 text-base font-light leading-relaxed text-[#C4BBB0]">
          Non è un fotografo generico: scatta pensando a come vende una struttura ricettiva. Ogni foto nasce per rispondere alla domanda dell'ospite: "ci starei bene, qui?"
        </p>
        <a
          href={ARCHIFOTOGRAFIA_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="fotografo-portfolio-link"
          className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#383029] px-5 py-2.5 text-sm text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#E05A2C]"
        >
          Guarda il portfolio di Archifotografia
          <ArrowUpRight className="h-4 w-4" />
        </a>

        <div className="mt-10">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { src: "/images/archifotografia/villa-1.webp", alt: "Villa con piscina fotografata da Archifotografia" },
              { src: "/images/archifotografia/interno-1.jpg", alt: "Interno di struttura ricettiva fotografato da Archifotografia" },
              { src: "/images/archifotografia/villa-2.webp", alt: "Dettaglio esterno di villa fotografata da Archifotografia" },
              { src: "/images/archifotografia/interno-2.jpg", alt: "Camera di B&B fotografata da Archifotografia" },
              { src: "/images/archifotografia/villa-3.webp", alt: "Esterno al tramonto fotografato da Archifotografia" },
              { src: "/images/archifotografia/interno-3.jpg", alt: "Soggiorno di casa vacanze fotografato da Archifotografia" },
            ].map((p, i) => (
              <div key={p.src} data-testid={`archifotografia-photo-${i + 1}`} className="overflow-hidden border border-[#2A241F]">
                <img src={p.src} alt={p.alt} loading="lazy" className="aspect-[4/3] h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-[#A89F91]">
            Foto: Giovanni Costagliola · Archifotografia
          </p>
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">La differenza con YU YieldUp</h2>
        <p className="mt-6 text-base font-light leading-relaxed text-[#C4BBB0]">
          Chiunque può consegnarti un pacchetto di foto. Noi le mettiamo dentro una strategia: quali foto aprono l'annuncio, quali chiudono la vendita, come si combinano con prezzi e testi. Le foto di Giovanni entrano direttamente nel lavoro quotidiano sulla tua struttura.
        </p>

        <div className="mt-10 border border-[#4D2C20] bg-[#1A1714] p-6 sm:p-8">
          <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Vuoi capire se le tue foto ti stanno costando prenotazioni?</p>
          <p className="mt-3 text-sm font-light leading-relaxed text-[#C4BBB0] sm:text-base">
            Incolla il link del tuo annuncio nel modulo in alto: entro 48 ore Giovanni ti dice su WhatsApp quali 3 foto cambiare. Se le tue foto sono già giuste, te lo diciamo e amen.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              data-testid="fotografo-cta-call"
              onClick={() => scrollToId("check-foto")}
              className="btn-glow min-h-[44px] rounded-full bg-[#E05A2C] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
            >
              Fai valutare le foto
            </button>
            <Link to="/" className="text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#E05A2C]">
              Scopri tutto il lavoro di YU YieldUp
            </Link>
          </div>
        </div>

        <h2 className="mt-14 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">Domande frequenti</h2>
        <PageFaq items={FAQ} />
      </section>
    </SubPageShell>
  );
}
